import React, { useState } from 'react';

const PaymentFlowAnalyzer = () => {
  const [interceptedRequests, setInterceptedRequests] = useState([]);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [originalFetch, setOriginalFetch] = useState(null);
  const [paymentAttempts, setPaymentAttempts] = useState([]);

  const addLog = (message, type = 'info', data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    const newEntry = {
      id: Date.now() + Math.random(),
      timestamp,
      message,
      type,
      data
    };
    setInterceptedRequests(prev => [...prev, newEntry]);
    console.log(`[${timestamp}] ${message}`, data);
  };

  const startDetailedMonitoring = () => {
    if (isMonitoring) return;

    addLog('🔍 Starting detailed payment flow monitoring...', 'info');
    
    // Store original fetch
    const originalFetchRef = window.fetch;
    setOriginalFetch(() => originalFetchRef);
    
    // Enhanced fetch interceptor
    window.fetch = async function(...args) {
      const [url, options] = args;
      const urlString = typeof url === 'string' ? url : url.toString();
      
      // Check if this is a payment-related request
      const isPaymentRequest = (
        urlString.includes('/checkout/onepage/payment') ||
        urlString.includes('/checkout/onepage/place_order') ||
        urlString.includes('/payment') ||
        urlString.includes('/place_order') ||
        urlString.includes('checkout') ||
        urlString.includes('order')
      );

      if (isPaymentRequest) {
        const requestInfo = {
          url: urlString,
          method: options?.method || 'GET',
          headers: options?.headers || {},
          hasBody: !!options?.body,
          bodyPreview: options?.body ? 
            (typeof options.body === 'string' ? 
              options.body.substring(0, 200) + '...' : 
              '[FormData or other body type]'
            ) : null
        };

        addLog(`🔍 Payment request intercepted: ${urlString.split('/').pop()}`, 'info', requestInfo);

        try {
          const response = await originalFetchRef.apply(this, args);
          const responseClone = response.clone();
          
          // Log response details
          const responseInfo = {
            status: response.status,
            statusText: response.statusText,
            headers: Object.fromEntries(response.headers.entries()),
            url: urlString
          };

          if (response.status >= 400) {
            addLog(`🚨 HTTP Error Response: ${response.status}`, 'error', responseInfo);
          } else {
            addLog(`✅ Successful Response: ${response.status}`, 'success', responseInfo);
          }

          // Try to read and analyze response content
          try {
            const responseText = await responseClone.text();
            const responseAnalysis = analyzePaymentResponse(responseText, response);
            
            if (responseAnalysis.isFailure) {
              addLog(`🚨 PAYMENT FAILURE DETECTED: ${responseAnalysis.failureType}`, 'error', {
                failureReason: responseAnalysis.reason,
                failureType: responseAnalysis.failureType,
                contentPreview: responseText.substring(0, 500) + '...',
                fullContent: responseText
              });
              
              // Record this as a payment attempt
              setPaymentAttempts(prev => [...prev, {
                id: Date.now(),
                timestamp: new Date().toISOString(),
                url: urlString,
                status: response.status,
                isFailure: true,
                failureReason: responseAnalysis.reason,
                responseContent: responseText
              }]);
              
            } else if (responseAnalysis.isSuccess) {
              addLog(`✅ PAYMENT SUCCESS DETECTED`, 'success', {
                successType: responseAnalysis.successType,
                contentPreview: responseText.substring(0, 500) + '...'
              });
              
              setPaymentAttempts(prev => [...prev, {
                id: Date.now(),
                timestamp: new Date().toISOString(),
                url: urlString,
                status: response.status,
                isFailure: false,
                responseContent: responseText
              }]);
              
            } else {
              addLog(`⚠️ AMBIGUOUS PAYMENT RESPONSE`, 'warning', {
                contentLength: responseText.length,
                contentPreview: responseText.substring(0, 500) + '...',
                hasErrorKeywords: /error|fail|decline|invalid/i.test(responseText),
                hasSuccessKeywords: /success|complete|approved|confirm/i.test(responseText)
              });
            }
            
          } catch (readError) {
            addLog(`⚠️ Could not read response content: ${readError.message}`, 'warning');
          }
          
          return response;
          
        } catch (error) {
          addLog(`❌ Payment request failed: ${error.message}`, 'error', { 
            url: urlString, 
            error: error.message 
          });
          throw error;
        }
      }
      
      // For non-payment requests, use original fetch
      return originalFetchRef.apply(this, args);
    };
    
    setIsMonitoring(true);
    addLog('✅ Enhanced payment monitoring started', 'success');
  };

  const stopMonitoring = () => {
    if (!isMonitoring || !originalFetch) return;
    
    addLog('⏹️ Stopping payment monitoring...', 'info');
    window.fetch = originalFetch;
    setIsMonitoring(false);
    addLog('✅ Payment monitoring stopped', 'success');
  };

  const analyzePaymentResponse = (responseText, response) => {
    const analysis = {
      isFailure: false,
      isSuccess: false,
      failureType: null,
      successType: null,
      reason: null
    };

    // Check HTTP status first
    if (response.status >= 400) {
      analysis.isFailure = true;
      analysis.failureType = 'http_error';
      analysis.reason = `HTTP ${response.status}: ${response.statusText}`;
      return analysis;
    }

    // Enhanced failure patterns - more comprehensive
    const failurePatterns = [
      // Direct card decline messages
      { pattern: /card.*declined/i, reason: 'Card declined by issuer', type: 'card_decline' },
      { pattern: /declined.*card/i, reason: 'Card declined', type: 'card_decline' },
      { pattern: /insufficient.*funds/i, reason: 'Insufficient funds', type: 'card_decline' },
      { pattern: /invalid.*card/i, reason: 'Invalid card information', type: 'card_decline' },
      { pattern: /expired.*card/i, reason: 'Card expired', type: 'card_decline' },
      { pattern: /card.*expired/i, reason: 'Card expired', type: 'card_decline' },
      { pattern: /cvv.*invalid/i, reason: 'Invalid CVV', type: 'card_decline' },
      { pattern: /invalid.*cvv/i, reason: 'Invalid CVV', type: 'card_decline' },
      { pattern: /cvc.*incorrect/i, reason: 'Incorrect CVC', type: 'card_decline' },
      
      // Payment processing errors
      { pattern: /payment.*failed/i, reason: 'Payment processing failed', type: 'processing_error' },
      { pattern: /transaction.*failed/i, reason: 'Transaction failed', type: 'processing_error' },
      { pattern: /payment.*declined/i, reason: 'Payment declined', type: 'processing_error' },
      { pattern: /transaction.*declined/i, reason: 'Transaction declined', type: 'processing_error' },
      { pattern: /authorization.*failed/i, reason: 'Authorization failed', type: 'processing_error' },
      { pattern: /payment.*error/i, reason: 'Payment error', type: 'processing_error' },
      
      // Gateway specific errors
      { pattern: /gateway.*error/i, reason: 'Payment gateway error', type: 'gateway_error' },
      { pattern: /processing.*error/i, reason: 'Processing error', type: 'gateway_error' },
      { pattern: /network.*error/i, reason: 'Network error', type: 'gateway_error' },
      { pattern: /timeout.*error/i, reason: 'Payment timeout', type: 'gateway_error' },
      
      // Form validation errors
      { pattern: /please.*check.*card/i, reason: 'Card validation error', type: 'validation_error' },
      { pattern: /invalid.*payment/i, reason: 'Invalid payment information', type: 'validation_error' },
      { pattern: /payment.*information.*incorrect/i, reason: 'Incorrect payment information', type: 'validation_error' },
      
      // JSON API errors
      { pattern: /"success":\s*false/i, reason: 'API returned failure', type: 'api_error' },
      { pattern: /"error":\s*"[^"]+"/i, reason: 'API error response', type: 'api_error' },
      { pattern: /"status":\s*"(failed|declined|error)"/i, reason: 'API status indicates failure', type: 'api_error' },
      
      // Generic error indicators
      { pattern: /error.*code.*\d+/i, reason: 'System error code', type: 'system_error' },
      { pattern: /unable.*to.*process/i, reason: 'Unable to process payment', type: 'system_error' },
      { pattern: /transaction.*could.*not.*be.*completed/i, reason: 'Transaction could not be completed', type: 'system_error' },
      
      // E-commerce specific patterns
      { pattern: /order.*could.*not.*be.*placed/i, reason: 'Order could not be placed', type: 'order_error' },
      { pattern: /checkout.*failed/i, reason: 'Checkout failed', type: 'order_error' },
      { pattern: /payment.*method.*declined/i, reason: 'Payment method declined', type: 'order_error' }
    ];

    // Check for failure patterns
    for (const { pattern, reason, type } of failurePatterns) {
      if (pattern.test(responseText)) {
        analysis.isFailure = true;
        analysis.failureType = type;
        analysis.reason = reason;
        
        // Try to extract more specific error message
        const match = responseText.match(pattern);
        if (match && match[0]) {
          analysis.reason = `${reason}: ${match[0]}`;
        }
        
        return analysis;
      }
    }

    // Check for success patterns
    const successPatterns = [
      { pattern: /payment.*successful/i, type: 'payment_success' },
      { pattern: /transaction.*approved/i, type: 'transaction_success' },
      { pattern: /order.*completed/i, type: 'order_success' },
      { pattern: /payment.*confirmed/i, type: 'payment_success' },
      { pattern: /thank.*you.*for.*your.*order/i, type: 'order_success' },
      { pattern: /"success":\s*true/i, type: 'api_success' },
      { pattern: /"status":\s*"success"/i, type: 'api_success' },
      { pattern: /order.*number.*\d+/i, type: 'order_success' }
    ];

    for (const { pattern, type } of successPatterns) {
      if (pattern.test(responseText)) {
        analysis.isSuccess = true;
        analysis.successType = type;
        return analysis;
      }
    }

    return analysis;
  };

  const clearLogs = () => {
    setInterceptedRequests([]);
    setPaymentAttempts([]);
  };

  const exportAnalysis = () => {
    const analysisData = {
      timestamp: new Date().toISOString(),
      totalRequests: interceptedRequests.length,
      paymentAttempts: paymentAttempts,
      interceptedRequests: interceptedRequests
    };
    
    const blob = new Blob([JSON.stringify(analysisData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-flow-analysis-${new Date().toISOString().slice(0,10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getLogStyle = (type) => {
    const baseStyle = {
      padding: '8px 12px',
      margin: '2px 0',
      borderRadius: '4px',
      fontSize: '14px',
      borderLeft: '4px solid',
      fontFamily: 'monospace'
    };

    switch (type) {
      case 'success':
        return { ...baseStyle, backgroundColor: '#d4edda', borderLeftColor: '#28a745', color: '#155724' };
      case 'error':
        return { ...baseStyle, backgroundColor: '#f8d7da', borderLeftColor: '#dc3545', color: '#721c24' };
      case 'warning':
        return { ...baseStyle, backgroundColor: '#fff3cd', borderLeftColor: '#ffc107', color: '#856404' };
      default:
        return { ...baseStyle, backgroundColor: '#d1ecf1', borderLeftColor: '#17a2b8', color: '#0c5460' };
    }
  };

  const buttonStyle = {
    padding: '8px 16px',
    margin: '4px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px'
  };

  return (
    <div style={{ 
      backgroundColor: 'white', 
      padding: '24px', 
      borderRadius: '8px', 
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
      marginBottom: '24px' 
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px' 
      }}>
        <h2 style={{ margin: 0, color: '#333' }}>
          🕵️ Real Payment Flow Analyzer
        </h2>
        <div style={{ 
          padding: '4px 12px', 
          backgroundColor: isMonitoring ? '#d4edda' : '#e9ecef', 
          borderRadius: '16px', 
          fontSize: '12px',
          color: isMonitoring ? '#155724' : '#6c757d'
        }}>
          {isMonitoring ? 'Active' : 'Inactive'}
        </div>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <div style={{ 
          padding: '16px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px',
          marginBottom: '16px'
        }}>
          <h4 style={{ margin: '0 0 8px 0' }}>📋 Instructions</h4>
          <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
            <li>Click "Start Monitoring" below</li>
            <li>Go to your checkout page and attempt a payment</li>
            <li>Use a test card that should fail (like 4000000000000002)</li>
            <li>Watch the logs below to see what requests are made</li>
            <li>Check if payment failures are detected</li>
          </ol>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <button
            style={{
              ...buttonStyle,
              backgroundColor: isMonitoring ? '#dc3545' : '#28a745',
              color: 'white'
            }}
            onClick={isMonitoring ? stopMonitoring : startDetailedMonitoring}
          >
            {isMonitoring ? '⏹️ Stop Monitoring' : '▶️ Start Monitoring'}
          </button>
          
          <button
            style={{
              ...buttonStyle,
              backgroundColor: '#6c757d',
              color: 'white'
            }}
            onClick={clearLogs}
          >
            🗑️ Clear Logs
          </button>
          
          <button
            style={{
              ...buttonStyle,
              backgroundColor: '#17a2b8',
              color: 'white'
            }}
            onClick={exportAnalysis}
          >
            💾 Export Analysis
          </button>
        </div>

        {paymentAttempts.length > 0 && (
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#fff3cd', 
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <h4 style={{ margin: '0 0 8px 0' }}>🎯 Payment Attempts Detected: {paymentAttempts.length}</h4>
            {paymentAttempts.map((attempt, index) => (
              <div key={attempt.id} style={{ 
                fontSize: '14px',
                marginBottom: '4px',
                color: attempt.isFailure ? '#dc3545' : '#28a745'
              }}>
                {index + 1}. {attempt.isFailure ? '❌ FAILURE' : '✅ SUCCESS'}: {attempt.failureReason || 'Success'} 
                <span style={{ fontSize: '12px', color: '#6c757d' }}>
                  ({new Date(attempt.timestamp).toLocaleTimeString()})
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 style={{ marginBottom: '16px' }}>📝 Request Log ({interceptedRequests.length})</h3>
        
        <div style={{ 
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          maxHeight: '400px',
          overflow: 'auto',
          backgroundColor: '#f8f9fa'
        }}>
          {interceptedRequests.length === 0 ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#6c757d' 
            }}>
              No payment requests captured yet. Start monitoring and make a payment to see the flow.
            </div>
          ) : (
            <div>
              {interceptedRequests.slice(-30).map((request) => (
                <div key={request.id}>
                  <div style={getLogStyle(request.type)}>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>
                      {request.timestamp}
                    </div>
                    <div>{request.message}</div>
                    {request.data && (
                      <details style={{ marginTop: '8px' }}>
                        <summary style={{ cursor: 'pointer', fontSize: '12px' }}>
                          View Details
                        </summary>
                        <pre style={{ 
                          fontSize: '11px', 
                          overflow: 'auto', 
                          backgroundColor: 'rgba(0,0,0,0.1)', 
                          padding: '8px',
                          borderRadius: '4px',
                          marginTop: '4px'
                        }}>
                          {JSON.stringify(request.data, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentFlowAnalyzer;